<template>
    <div>
        <page-header :title="title" :items="breadcrumbs" />

        <div class="my-3 d-flex justify-content-start">
            <div>
                <button
                    v-b-modal.add-edit-attribute-group
                    class="btn btn-success waves-effect waves-light mr-2"
                >
                    <i class="mdi mdi-plus-circle mr-1" />
                    Add Group
                </button>
                <template v-if="activeGroup.id">
                    <button
                        class="btn btn-info waves-effect waves-light mr-2"
                        @click="openEdit"
                    >
                        <i class="mdi mdi-plus-circle mr-1" />
                        Edit Group
                    </button>
                    <button
                        class="btn btn-danger waves-effect waves-light mr-2"
                        @click="showDeleteModal"
                    >
                        <i class="mdi mdi-plus-circle mr-1" />
                        Remove Group
                    </button>
                </template>
            </div>
        </div>

        <tabs
            v-if="groups.length"
            :items="groups"
            :active-item="activeGroup"
            label="name"
            value-label="id"
            is-card
            class="mb-3"
            @item-change="activeGroup = $event"
        />

        <awesome-table
            v-if="activeGroup.id"
            :labels="labels"
            :items="items"
            :options="options"
            :is-loading="isLoading"
            @row-click="onRowClick"
            @add-button-click="onAddButtonClick"
        />

        <confirmation-modal
            id="remove-group-modal"
            @agree="removeGroup"
            @disagree="idToDelete = null"
        />

        <add-edit-group-modal
            :group="editMode ? activeGroup : null"
            @close="editMode = false"
            @stored="groupStored"
            @updated="groupUpdated"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import Tabs from '@/components/elements/Tabs';
import ConfirmationModal from '@/components/elements/ConfirmationModal';
import AddEditGroupModal from '@/components/attribute-groups/AddEditModal';

export default {
    page: {
        title: 'Attributes'
    },

    components: {
        Tabs,
        ConfirmationModal,
        AddEditGroupModal
    },

    data() {
        return {
            title: 'Attributes',
            breadcrumbs: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Attributes',
                    active: true
                }
            ],
            groups: [],
            activeGroup: {},
            isLoading: false,
            items: [],
            labels: ['value', { value: 'createdAt', filter: 'formatDate' }],
            options: {
                pagination: false,
                searchInput: false,
                filters: false,
                link: false,
                sort: false
            },
            idToDelete: null,
            editMode: false
        };
    },

    watch: {
        activeGroup() {
            this.fetchAttributes();
        }
    },

    created() {
        this.fetchGroups();
    },

    methods: {
        ...mapActions({
            getAttributeGroups: 'attributeGroups/index',
            deleteAttributeGroups: 'attributeGroups/destroy',
            getAttributes: 'attributes/index'
        }),

        async fetchGroups() {
            try {
                this.groups = await this.getAttributeGroups();

                const { attributeGroupId } = this.$route.query;

                if (attributeGroupId) {
                    this.activeGroup = this.groups.find(
                        item => item.id === attributeGroupId
                    );
                }
            } catch (err) {
                console.error(err);

                this.$toasterError();
            }
        },

        async fetchAttributes() {
            this.isLoading = true;

            try {
                this.items = await this.getAttributes(this.activeGroup.id);
            } catch (err) {
                console.error(err);

                this.$toasterError();
            }

            this.isLoading = false;
        },

        showDeleteModal() {
            const { id } = this.activeGroup;

            if (!id) {
                return;
            }

            this.idToDelete = id;
            this.$bvModal.show('remove-group-modal');
        },

        async removeGroup() {
            try {
                await this.deleteAttributeGroups(this.idToDelete);

                this.activeGroup = {};
                this.groups = this.groups.filter(
                    item => item.id !== this.idToDelete
                );
                this.idToDelete = null;

                this.$toaster('Group has been deleted.');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        groupStored(group) {
            this.groups = [...this.groups, group];
            this.activeGroup = { ...group };
        },

        async openEdit() {
            this.editMode = true;

            await this.$nextTick();

            this.$bvModal.show('add-edit-attribute-group');
        },

        groupUpdated(group) {
            const index = this.groups.findIndex(
                item => item.id === this.activeGroup.id
            );

            this.groups.splice(index, 1, group);
        },

        onRowClick({ id }) {
            this.$router.push(`/attributes/${id}/edit`);
        },
        onAddButtonClick() {
            this.$router.push(
                `/attributes/add?attributeGroupId=${this.activeGroup.id}`
            );
        }
    }
};
</script>
